import {Component, OnDestroy, OnInit, signal} from '@angular/core';
import {CodaltComponent} from '../../codalt.component';
import {ArticleService} from '../../services/article.service';
import {Article} from '../../classes/article.class';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {DateNLFormatAdapter} from '../../date-adapters/date-nlformat-adapter';
import {DateAdapter} from '@angular/material/core';
import {formatDate, Location} from '@angular/common';
import {Routenames} from '../../route-names.enum';
import {Utils} from '../../utils.class';
import {ConfirmDialogService} from '../../services/confirm-dialog-service/confirm-dialog.service';
import {validTime} from '../../validators/valid-time.validator';
import {handleTimeValueChange} from '../../utils/handle-time-value-change';
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList} from '@angular/cdk/drag-drop';
import {ArticleImage} from '../../classes/article-image.class';
import {VvrIconComponent} from "../../vvr-icon/vvr-icon.component";
import {MatTooltip} from "@angular/material/tooltip";
import {CodaltImageComponent} from "../../codalt-image/codalt-image.component";
import {FileManagerComponent} from "../../file-manager/file-manager.component";
import {MatError, MatFormField, MatInput, MatLabel} from "@angular/material/input";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {SimpleTinyComponent} from "../../simple-tiny/simple-tiny.component";
import {MatRadioButton, MatRadioGroup} from "@angular/material/radio";
import {MatFabButton, MatMiniFabButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatCheckbox} from "@angular/material/checkbox";

@Component({
    selector: 'app-article-edit',
    templateUrl: './article-edit.component.html',
    styleUrls: ['./article-edit.component.scss'],
    imports: [
        VvrIconComponent,
        MatTooltip,
        RouterLink,
        ReactiveFormsModule,
        CodaltImageComponent,
        FileManagerComponent,
        MatFormField,
        MatLabel,
        MatInput,
        MatError,
        MatDatepickerInput,
        MatDatepickerToggle,
        MatDatepicker,
        SimpleTinyComponent,
        MatRadioGroup,
        MatRadioButton,
        CdkDropList,
        CdkDrag,
        MatMiniFabButton,
        MatIcon,
        CdkDragHandle,
        MatCheckbox,
        MatFabButton
    ],
    providers: [
        {provide: DateAdapter, useClass: DateNLFormatAdapter}
    ]
})
export class ArticleEditComponent extends CodaltComponent implements OnInit, OnDestroy {

    mainSchool = false;
    reserve = false;
    readonly saving = signal<boolean>(false);
    readonly form = signal<FormGroup>(null);
    article: Article;

    constructor(
        private articleService: ArticleService,
        private location: Location,
        private router: Router,
        private dialog: ConfirmDialogService,
        private confirmDialog: ConfirmDialogService,
        private route: ActivatedRoute
    ) {
        super();
    }

    reorderImages(event: CdkDragDrop<ArticleImage[]>) {
        const movedImage = this.article.images[event.previousIndex];
        this.article.images.splice(event.previousIndex, 1);
        this.article.images.splice(event.currentIndex, 0, movedImage);
        this.articleService.sortImages(this.article).subscribe(() => {
        });
    }

    ngOnInit(): void {
        this.subscriptions.add(this.route.params.subscribe(params => {
            this.getArticle(params['id']);
        }));
    }

    public openChangesBackActionCheck(): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.form().dirty) {
                this.dialog.confirm(
                    'Niet opgeslagen wijzigingen',
                    `Wilt u de niet opgeslagen wijzigingen opslaan of verwerpen?`,
                    'Wijzigingen opslaan',
                    'Wijzigingen verwerpen').then(() => {
                    this.save();
                    resolve(false);
                }, () => {
                    resolve(true);
                });
            } else {
                resolve(true);
            }
        });
    }

    save() {
        Utils.triggerValidation(this.form());
        if (this.form().valid) {
            this.saving.set(true);
            this.articleService.save(this.form().value).subscribe(article => {
                Object.assign(this.article, article.data);
                this.form().markAsPristine();
                this.saving.set(false);
                if (new Date(this.form().value['publish_date']).getTime() > new Date().getTime()) {
                    this.router.navigateByUrl(`${Routenames.articles}/toekomst`);
                } else {
                    this.router.navigateByUrl(Routenames.articles);
                }

            }, () => {
                this.saving.set(false);
            });
        }
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    uploadedFiles(files) {
        if (!this.article.images) {
            this.article.images = [];
        }
        files.file.forEach(file => {
            this.articleService.addImage(this.article.id, file.filename).subscribe(img => {
                const loading = this.article.images.find(p => !p.id);
                if (loading) {
                    img.data.thumb = loading.thumb;
                    Object.assign(loading, img.data);
                } else {
                    this.article.images.push(img.data);
                }
            });
        });
    }

    uploadFiles(files?: File[]) {
        Array.from(files).forEach(file => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                if (!this.article.images) {
                    this.article.images = [];
                }
                this.article.images.push({
                    thumb: reader.result.toString()
                } as ArticleImage);
            };
        });
    }

    deleteImage(image: ArticleImage) {
        this.articleService.rmImage(image.id).subscribe(() => {
            const index = this.article.images.indexOf(image);
            this.article.images.splice(index, 1);
        });
    }

    uploadedMainImage(file) {
        this.form().value.image = file ? file.file.filename : null;
    }

    uploadMainImage(files) {
        const reader = new FileReader();
        reader.readAsDataURL(files[0]);
        reader.onload = () => {
            this.article.image_thumb = reader.result.toString();
        };
        this.form().value.image = null;
    }

    private getArticle(slug) {
        this.reserve = slug === 'nieuw';
        const article$ = this.reserve ? this.articleService.reserve() : this.articleService.get(slug, this.route.snapshot.url[1].path === 'kopie');
        article$.subscribe(article => {
            this.article = article.data;
            this.generateForm(this.article);
        }, error => {
            this.dialog.confirm('Fout', error.message, 'Terug naar overzicht', null).then(() => {
                this.router.navigate([Routenames.articles]);
            });
        });
    }

    private generateForm(article: Article) {
        const images = article.images ? article.images.map(img => img.id) : [];
        const fcAccessLevel = new FormControl(article.access_level ? article.access_level : 'EVERYONE');
        if (!article.publish_date) {
            article.publish_date = new Date();
            if (article.publish_date.getMinutes() > 44) {
                article.publish_date.setHours(article.publish_date.getHours() + 2);
            } else {
                article.publish_date.setHours(article.publish_date.getHours() + 1);
            }
            article.publish_date.setMinutes(0);
        }
        const fcPublishTime = new FormControl(
            article.publish_date ? formatDate(article.publish_date, 'HH:mm', 'nl') : null,
            {validators: [validTime(), Validators.required], updateOn: 'blur'}
        );
        const fcPublishDate = new FormControl(article.publish_date ? article.publish_date : new Date());
        this.form.set(
            new FormGroup({
                id: new FormControl(article.id),
                title: new FormControl(article.title, Validators.required),
                alert: new FormControl(article.alert || false),
                content: new FormControl(article.content, Validators.required),
                publish_date: fcPublishDate,
                publish_time: fcPublishTime,
                images: new FormControl(images),
                access_level: fcAccessLevel,
                image: new FormControl(article.image)
            })
        );
        this.subscriptions.add(fcPublishTime.valueChanges.subscribe(
            value => handleTimeValueChange(value, fcPublishTime)
        ));
        this.subscriptions.add(this.form().valueChanges.subscribe(() => {
            if (fcPublishTime.value && fcPublishDate.value) {
                const time = fcPublishTime.value.split(':');
                if (time.length === 2) {
                    const date = new Date(fcPublishDate.value);
                    date.setHours(Number(time[0]));
                    date.setMinutes(Number(time[1]));
                    if (date.toString() !== (new Date(fcPublishDate.value)).toString()) {
                        fcPublishDate.setValue(date);
                    }
                }
            }
        }));
    }
}
